import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { useFetch } from "services/hooks/useFetch";
import { CustomTextBox, CustomAutoComplete } from "components/CustomComponents";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import RequestStatusTimeline from "components/DesignSystem/RequestStatusTimeline";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import {
  designColors,
  designRadii,
  designShell,
} from "services/constants/designTokens";
import {
  GlobalFieldLabel,
  GlobalConfirmDialog,
} from "components/DesignSystem/sharedUiPatterns";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { canonicalOffDayDutyStatus, offDayDutyStatusVisual } from "./OffDayDutyStatusCards";

const dayoffType = [{ label: "Week Off" }, { label: "Holiday" }];

const toISODate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

const detailAccent = {
  dutyType: detailAccentPalette.blue,
  date: detailAccentPalette.indigo,
  mode: detailAccentPalette.green,
  reason: detailAccentPalette.orange,
  rejectReason: detailAccentPalette.purple,
  cancelReason: detailAccentPalette.purple,
};

const ApprovalModeChip = ({ mode }) => {
  const isPlanned = mode === "Planned";
  const accent = isPlanned ? "#2563EB" : "#D97706";
  const tint = isPlanned ? "#EFF6FF" : "#FFFBEB";
  return (
    <Mui.Chip
      size="small"
      label={mode || "N/A"}
      sx={{
        height: 26,
        borderRadius: designRadii.pill,
        backgroundColor: tint,
        color: accent,
        border: `1px solid ${accent}`,
        "& .MuiChip-label": { px: 1, fontSize: 12.5, fontWeight: 600 },
      }}
    />
  );
};

export const OffDayDutyDetailPanel = ({
  open,
  onClose,
  request,
  checkAccess,
  setReCallApi,
  expanded = false,
  onToggleExpand,
}) => {
  const goToApprovalCenter = useGoToApprovalCenter();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localOverride, setLocalOverride] = useState(null);
  // No dedicated cancel-an-approved-off-day-duty endpoint exists yet, same
  // gap as Comp Off (see CompOffDetailPanel) — this reuses the same
  // approve/reject endpoint below with status: "Cancelled" as a first
  // attempt, needs verifying against the real backend.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const { domain, userDetails, topbarCompanyId, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  // See Leave/LeaveDetailPanel.js — Approve/Reject/Cancel (and the
  // edit-then-approve submitEdit flow below, which always sends
  // status:"Approved") used to be gated by plain editor permission
  // (checkAccess). Now requires real approver permission (either level
  // counts).
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Off Day Duty",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Off Day Duty",
    );
  // The Edit trigger below used to render for anyone viewing a pending
  // request, with no permission check at all — even less gated than
  // Overtime's equivalent was before its fix. Editing the request's own
  // fields is a distinct capability from approving it, so it needs both:
  // editor access to be allowed to modify fields at all, and approver
  // access since submitEdit always finalizes as status: "Approved".
  const canEditRequest = checkAccess && hasApproverAccess;
  const { result: employees } = useFetch(
    `${domain}list/employee?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({ mode: "onBlur", defaultValues: { rejected_reason: "" } });

  useEffect(() => {
    register("rejected_reason", {
      required: "Reject reason is required",
      validate: {
        minLengthNoSpaces: (value) => {
          if (value?.trim().length < 3) return minimum3Characters;
          if (value?.trim().length > 255) return "Maximum length is 255 characters";
          return true;
        },
        atleastOneCharacterWithSpaceReg: (value) =>
          atleastOneCharacterWithSpace.test(value) || "Atleast one alphabet required",
      },
    });
  }, [register]);

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    setValue: setEditValue,
    watch: watchEdit,
    control: editControl,
    reset: resetEdit,
    formState: { errors: editErrors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      start_date: null,
      end_date: null,
      dayoff_type: "",
      reason: "",
    },
  });
  const [showEditAvatar, setShowEditAvatar] = useState(false);

  const displayRequest = localOverride || request;

  useEffect(() => {
    setRejectDialogOpen(false);
    setEditMode(false);
    setLocalOverride(null);
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
    reset({ rejected_reason: "" });
    resetEdit({
      user_name: request?.employee_name || "",
      user_id: request?.user_id,
      start_date: request?.date ? dayjs(toISODate(request.date)) : null,
      end_date: request?.date ? dayjs(toISODate(request.date)) : null,
      dayoff_type: request?.day_type || "",
      reason: request?.reason || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, reset, resetEdit]);

  const statusFlowData = useMemo(
    () => ({
      ...(displayRequest || {}),
      applied_by:
        displayRequest?.applied_by ||
        displayRequest?.employee_name ||
        displayRequest?.username ||
        displayRequest?.requested_by,
      applied_on:
        displayRequest?.applied_on ||
        displayRequest?.applied_date ||
        displayRequest?.created_at ||
        displayRequest?.created_date ||
        displayRequest?.date,
      // displayRequest?.approver_name would be the assigned approver,
      // stamped at apply time before anyone has acted -- it is not proof of
      // approval. The backend doesn't currently send this field for Day-off,
      // but preferring the status-derived approved_by is the correct
      // priority regardless, matching every other detail panel.
      approved_by: displayRequest?.approved_by,
      approved_at: displayRequest?.approved_at || displayRequest?.approved_on,
      rejected_by: displayRequest?.rejected_by,
      rejected_at: displayRequest?.rejected_at || displayRequest?.rejected_on,
    }),
    [displayRequest],
  );

  if (!request) return null;

  const status = canonicalOffDayDutyStatus(displayRequest?.status);
  const isPending = status === "Pending";
  // Requested per user: allow cancelling an off-day duty request even after
  // it has been Approved (not just while Pending), same as Comp Off.
  const canCancelApproved = status === "Approved";
  const employeeName = displayRequest?.employee_name || "N/A";
  const employeeId =
    displayRequest?.employee_id || displayRequest?.emp_id || "-";
  const department =
    displayRequest?.department_name || displayRequest?.department || "-";
  const designation =
    displayRequest?.designation_name || displayRequest?.designation || "-";
  const requestReason =
    displayRequest?.reason || displayRequest?.description || "-";
  const rejectedReason = displayRequest?.rejected_reason || "";
  const cancelledReason = displayRequest?.cancelled_reason || "";

  const handleCancelEdit = () => {
    resetEdit({
      user_name: request?.employee_name || "",
      user_id: request?.user_id,
      start_date: request?.date ? dayjs(toISODate(request.date)) : null,
      end_date: request?.date ? dayjs(toISODate(request.date)) : null,
      dayoff_type: request?.day_type || "",
      reason: request?.reason || "",
    });
    setEditMode(false);
  };

  const handleCloseAction = () => {
    if (editMode) {
      handleCancelEdit();
      return;
    }
    onClose?.();
  };

  const updateEditUserName = (event, data) => {
    if (data) {
      setEditValue("user_name", data?.label);
      setEditValue("user_id", data?.user_id);
      setEditValue("selectedUser", data);
      setShowEditAvatar(false);
    } else {
      setEditValue("user_name", null);
      setEditValue("user_id", "");
      setEditValue("selectedUser", null);
      setShowEditAvatar(true);
    }
  };

  const selectEditDayoffType = (event, data) => {
    if (data) {
      setEditValue("dayoff_type", data?.label);
    }
  };

  const submitEdit = async (data) => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this off-day duty request",
      );
      return;
    }
    setSaving(true);
    const payload = {
      pre_dayoff_ids: [request.id],
      user_id: data.user_id,
      start_date: data.start_date?.format("YYYY-MM-DD"),
      end_date: data.end_date?.format("YYYY-MM-DD"),
      reason: data.reason,
      approval_type: data.dayoff_type,
      status: "Approved",
    };
    const response = await customAPI(
      `${domain}team_zone/approve/day_off/teamworkspace-dayoff-approve/?company_id=${topbarCompanyId}`,
      "PUT",
      payload,
    );
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message);
      setLocalOverride((prev) => ({
        ...(prev || request || {}),
        employee_name: data.user_name || request?.employee_name,
        user_id: data.user_id || request?.user_id,
        date: data.start_date?.format("DD-MM-YYYY") || request?.date,
        day_type: data.dayoff_type || request?.day_type,
        reason: data.reason,
        status: "Approved",
      }));
      setReCallApi?.(true);
      setEditMode(false);
      onClose?.();
    }
    // On failure customAPI already surfaces the error toast.
    setSaving(false);
  };

  const updatePlannedStatus = async (requestStatus, reason) => {
    const payload = {
      pre_dayoff_ids: [request.id],
      date: toISODate(displayRequest?.date),
      approval_type: displayRequest?.day_type,
      status: requestStatus,
      ...(requestStatus === "Rejected" && { rejected_reason: reason }),
      ...(requestStatus === "Cancelled" && { cancelled_reason: reason }),
    };
    const response = await customAPI(
      `${domain}team_zone/approve/day_off/teamworkspace-dayoff-approve/?company_id=${topbarCompanyId}`,
      "PUT",
      payload,
    );
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message);
      setReCallApi?.(true);
      setRejectDialogOpen(false);
      setCancelDialogOpen(false);
      onClose?.();
      return true;
    }
    // On failure customAPI already surfaces the error toast.
    return false;
  };

  const handleApprove = async () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this off-day duty request",
      );
      return;
    }
    setApproving(true);
    await updatePlannedStatus("Approved");
    setApproving(false);
  };

  const submitReject = async (data) => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to reject this off-day duty request",
      );
      return;
    }
    setRejecting(true);
    await updatePlannedStatus("Rejected", data?.rejected_reason);
    setRejecting(false);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    reset({ rejected_reason: "" });
    clearErrors("rejected_reason");
  };

  const submitCancel = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to cancel this off-day duty request",
      );
      return;
    }
    setCancelling(true);
    await updatePlannedStatus("Cancelled", trimmed);
    setCancelling(false);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  const editContent = (
    <Mui.Box component="form" onSubmit={handleSubmitEdit(submitEdit)}>
      <Mui.Grid container spacing={2.5}>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Select the employee">
            Employee Name
          </GlobalFieldLabel>
          <CustomAutoCompleteWithLogo
            dataList={employees?.map((obj) => ({
              ...obj,
              label: obj.user_name,
              image: obj?.image_url,
            }))}
            selectedValue={watchEdit("user_name")}
            rules={{
              ...registerEdit("user_name", {
                required: "Employee name is required",
              }),
            }}
            updateDataFunction={updateEditUserName}
            placeHolder="Select employee name"
            errorMessage={editErrors?.user_name?.message || ""}
            textBoxError={Boolean(editErrors.user_name)}
            showAvathar={showEditAvatar}
            setShowAutoCompleteAvathar={setShowEditAvatar}
          />
        </Mui.Grid>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Update the start date for the off-day period">
            Start Date
          </GlobalFieldLabel>
          <CustomizedDatePicker
            name="start_date"
            control={editControl}
            required
            requiredMessage="Start date is required"
            placeholder="Select start date"
          />
        </Mui.Grid>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Update the end date for the off-day period">
            End Date
          </GlobalFieldLabel>
          <CustomizedDatePicker
            name="end_date"
            control={editControl}
            required
            requiredMessage="End date is required"
            placeholder="Select end date"
            startDate={watchEdit("start_date")}
          />
        </Mui.Grid>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Update the off-day duty type for further reference">
            Off-Day Duty Type
          </GlobalFieldLabel>
          <CustomAutoComplete
            dataList={dayoffType}
            rules={{
              ...registerEdit("dayoff_type", {
                required: "Dayoff type is required",
              }),
            }}
            selectedValue={watchEdit("dayoff_type")}
            updateDataFunction={selectEditDayoffType}
            placeHolder="Select dayoff type"
            errorMessage={editErrors?.dayoff_type?.message || ""}
            textBoxError={Boolean(editErrors.dayoff_type)}
          />
        </Mui.Grid>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Enter the reason for this off-day duty request">
            Reason
          </GlobalFieldLabel>
          <CustomTextBox
            type="multiline"
            rules={{
              ...registerEdit("reason", {
                required: "Reason is required",
                validate: {
                  minLengthNoSpaces: (value) => {
                    if (value?.trim().length < 3 && value !== "") {
                      return minimum3Characters;
                    }
                    if (value?.trim().length > 255 && value !== "") {
                      return "Maximum length is 255 characters";
                    }
                    return true;
                  },
                  atleastOneCharacterWithSpaceReg: (value) =>
                    atleastOneCharacterWithSpace.test(value) ||
                    value === "" ||
                    "Atleast one alphabet required",
                },
              }),
            }}
            selectedValue={watchEdit("reason")}
            updateDataFunction={(event) => {
              if (event.target.value.length <= 1024) {
                setEditValue("reason", event.target.value);
              }
            }}
            placeHolder="Provide a reason for off-day duty"
            errorMessage={editErrors?.reason?.message || ""}
            textBoxError={Boolean(editErrors.reason)}
          />
        </Mui.Grid>
      </Mui.Grid>
    </Mui.Box>
  );

  // Reject/Approve/Cancel below used to always render, only disabled
  // (via AccessGatedAction) when hasApproverAccess was false — and since
  // the outer Mui.Stack always rendered too, GlobalSidePanel's footer
  // border/background still showed even with nothing usable inside. Each
  // branch now resolves to real content or a genuine `null`, same fix as
  // OverTime/DetailPanel.js, so an editor-only viewer gets no footer at
  // all instead of disabled buttons or an empty bordered strip.
  const footerContent = editMode ? (
    <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
      <SecondaryButton onClick={handleCancelEdit}>Cancel</SecondaryButton>
      <PrimaryButton
        onClick={handleSubmitEdit(submitEdit)}
        loading={saving}
        disabled={saving}
      >
        Approve
      </PrimaryButton>
    </Mui.Stack>
  ) : isPending ? (
    hasApproverAccess ? (
      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1.5,
        }}
      >
        <Mui.Button
          variant="outlined"
          startIcon={<HighlightOffIcon />}
          onClick={() => setRejectDialogOpen(true)}
          sx={{
            ...designShell.panelDangerButton,
            color: "#B42318",
            borderColor: "#F5C2C7",
            backgroundColor: "#FEF3F2",
            "&:hover": {
              borderColor: "#E59CA5",
              backgroundColor: "#FEE4E2",
            },
          }}
        >
          Reject
        </Mui.Button>
        <PrimaryButton
          startIcon={<CheckCircleOutlineIcon />}
          loading={approving}
          onClick={handleApprove}
        >
          Approve
        </PrimaryButton>
      </Mui.Box>
    ) : null
  ) : canCancelApproved ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5}>
        <Mui.Typography
          sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
        >
          This request has already been approved.
        </Mui.Typography>
        <RequestCancelButton onClick={() => setCancelDialogOpen(true)}>
          Cancel Off-Day Duty
        </RequestCancelButton>
      </Mui.Stack>
    ) : null
  ) : (
    <Mui.Typography
      sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
    >
      This request has already been {status?.toLowerCase() || "processed"}.
    </Mui.Typography>
  );

  const rejectDialog = (
    <GlobalConfirmDialog
      open={rejectDialogOpen}
      title="Reject Off-Day Duty Request"
      onCancel={closeRejectDialog}
      onConfirm={() => {
        if (!watch("rejected_reason")?.trim()) {
          setError("rejected_reason", {
            type: "required",
            message: "Reject reason is required",
          });
          return;
        }
        handleSubmit(submitReject)();
      }}
      confirmLabel="Reject"
      cancelLabel="Cancel"
      loading={rejecting}
      danger
      reasonValue={watch("rejected_reason") || ""}
      onReasonChange={(value) =>
        setValue("rejected_reason", value, { shouldValidate: true })
      }
      reasonError={errors?.rejected_reason?.message || ""}
      reasonPlaceholder="Provide a reason for rejection"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Enter the reason for rejecting this off-day duty request.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const cancelDialog = (
    <GlobalConfirmDialog
      open={cancelDialogOpen}
      title="Cancel Off-Day Duty Request"
      onCancel={closeCancelDialog}
      onConfirm={submitCancel}
      confirmLabel="Continue"
      cancelLabel="Keep"
      loading={cancelling}
      loadingLabel="Cancelling..."
      danger
      reasonValue={cancelReason}
      onReasonChange={(value) => {
        setCancelReason(value);
        if (cancelReasonError) setCancelReasonError("");
      }}
      reasonError={cancelReasonError}
      reasonPlaceholder="Provide a reason for cancelling this approved off-day duty request"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        This off-day duty request has already been approved. Enter a reason
        to cancel it.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={handleCloseAction}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Off-Day Duty Request"
        subtitle="Request details and status"
        headerActions={
          isPending && !editMode && canEditRequest ? (
            <Mui.Tooltip title="Edit off-day duty request" arrow>
              <Mui.IconButton
                aria-label="Edit off-day duty request"
                size="small"
                onClick={() => setEditMode(true)}
                sx={{
                  ...designShell.panelSoftIconButton,
                  color: designColors.slate500,
                }}
              >
                <EditIcon fontSize="small" />
              </Mui.IconButton>
            </Mui.Tooltip>
          ) : null
        }
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "Off-Day Duty", onClick: handleCloseAction },
          employeeName || "Off-Day Duty Request",
        ]}
        footer={footerContent}
      >
        <Mui.Box
          sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}
        >
          {editMode ? (
            <>
              <RequestDetailHeader
                userDetails={{
                  image_url: displayRequest?.image_url,
                  employee_id: employeeId,
                  user_name: employeeName,
                  department_name: department,
                  designation,
                }}
                status={status}
                colorCode={displayRequest?.color_code || offDayDutyStatusVisual(status).accent}
              />
              <Mui.Typography
                sx={{ ...approvePanelUi.panelMutedText, px: 0.5 }}
              >
                Editing this off-day duty request&apos;s details.
              </Mui.Typography>
              {editContent}
            </>
          ) : (
            <>
              <RequestDetailHeader
                userDetails={{
                  image_url: displayRequest?.image_url,
                  employee_id: employeeId,
                  user_name: employeeName,
                  department_name: department,
                  designation,
                }}
                status={status}
                colorCode={displayRequest?.color_code || offDayDutyStatusVisual(status).accent}
              />

              <Mui.Box sx={detailCardSx}>
                <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
                  Request Details
                </Mui.Typography>
                <RequestDetailField
                  icon={CategoryOutlinedIcon}
                  label="Duty Type"
                  value={displayRequest?.day_type || "-"}
                  accent={detailAccent.dutyType}
                />
                <RequestDetailField
                  icon={CalendarMonthIcon}
                  label="Date"
                  value={displayRequest?.date || "-"}
                  accent={detailAccent.date}
                />
                <RequestDetailField
                  icon={CategoryOutlinedIcon}
                  label="Approval Mode"
                  value={displayRequest?.approval_mode || "-"}
                  accent={detailAccent.mode}
                  rightPill={
                    <ApprovalModeChip mode={displayRequest?.approval_mode} />
                  }
                />
                <RequestDetailField
                  icon={DescriptionOutlinedIcon}
                  label="Reason"
                  value={requestReason}
                  accent={detailAccent.reason}
                  multiline
                  isLast={!rejectedReason && !cancelledReason}
                />
                {rejectedReason ? (
                  <RequestDetailField
                    icon={DescriptionOutlinedIcon}
                    label="Reject Reason"
                    value={rejectedReason}
                    accent={detailAccent.rejectReason}
                    multiline
                    isLast
                  />
                ) : null}
                {cancelledReason ? (
                  <RequestDetailField
                    icon={DescriptionOutlinedIcon}
                    label="Cancel Reason"
                    value={cancelledReason}
                    accent={detailAccent.cancelReason}
                    multiline
                    isLast
                  />
                ) : null}
              </Mui.Box>

              <RequestStatusTimeline
            endDate={displayRequest?.date}
                data={statusFlowData}
                variant="stepper"
                hidePendingStep
              />
              <ApprovalEditAuditTrail data={displayRequest} />
            </>
          )}
        </Mui.Box>
      </GlobalSidePanel>
      {rejectDialog}
      {cancelDialog}
    </>
  );
};
